import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Send, ChevronDown, ChevronRight } from "lucide-react";

const STATUS_COLORS = { new: "bg-primary text-white", responded: "bg-accent text-foreground", closed: "bg-muted text-muted-foreground" };

export const InquiryConversation = ({ inquiry, viewerRole, onChange, subtitle, extraControls }) => {
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const markedRef = useRef(false);
  const bottomRef = useRef(null);
  const unread = inquiry.unread || 0;

  const messages = inquiry.messages && inquiry.messages.length
    ? inquiry.messages
    : [{ sender_role: "buyer", sender_name: inquiry.buyer_name, message: inquiry.message, created_at: inquiry.created_at }];

  useEffect(() => {
    if (open && bottomRef.current) bottomRef.current.scrollIntoView({ block: "nearest" });
  }, [open, messages.length]);

  const markRead = async () => {
    if (markedRef.current || unread === 0) return;
    markedRef.current = true;
    try {
      await api.post(`/inquiries/${inquiry.inquiry_id}/read`);
      onChange && onChange();
    } catch { /* noop */ }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) markRead();
  };

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/inquiries/${inquiry.inquiry_id}/messages`, { message: reply });
      setReply("");
      markedRef.current = false;
      toast.success("Reply sent");
      onChange && onChange();
      setTimeout(() => bottomRef.current?.scrollIntoView({ block: "nearest" }), 200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid={`conversation-${inquiry.inquiry_id}`}>
      <div className="flex items-center gap-3">
        <button onClick={toggle} className="flex items-center gap-2 flex-1 text-left" data-testid={`toggle-conversation-${inquiry.inquiry_id}`}>
          {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <span className="font-medium">{inquiry.product_title}</span>
          <Badge className={`rounded-none text-[10px] uppercase ${STATUS_COLORS[inquiry.status] || ""}`}>{inquiry.status}</Badge>
          {unread > 0 && (
            <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 bg-destructive text-white text-[11px] font-bold rounded-full" data-testid={`unread-badge-${inquiry.inquiry_id}`}>
              {unread}
            </span>
          )}
        </button>
        {extraControls}
      </div>
      {subtitle && <p className="text-xs text-muted-foreground mt-1 ml-6">{subtitle}</p>}

      {open && (
        <div className="mt-3 border-t border-border pt-3" data-testid={`thread-${inquiry.inquiry_id}`}>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {messages.map((m, i) => {
              const mine = m.sender_role === viewerRole;
              return (
                <div key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] p-2.5 text-sm border ${mine ? "bg-primary text-white border-primary" : "bg-muted border-border"}`}>
                    <div className={`text-[10px] uppercase tracking-wider mb-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                      {m.sender_role === "seller" ? "Seller" : "Buyer"} · {m.sender_name}
                    </div>
                    <div className="whitespace-pre-line">{m.message}</div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
          <div className="flex gap-2 mt-3">
            <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={1}
                      placeholder="Write a reply…" className="rounded-none min-h-[40px]" data-testid={`reply-input-${inquiry.inquiry_id}`} />
            <Button onClick={send} disabled={sending || !reply.trim()} className="rounded-none bg-primary hover:bg-primary/90 shrink-0" data-testid={`reply-send-${inquiry.inquiry_id}`}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
